import useUserInfo from "@/hooks/use-user-info";
import { lowerCaseComparison } from "@/lib/string-util";
import { closeMobileView } from "@/signals/mobile-view-signal";
import React, {
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";

const VITE_BASE_ZCONNECT_URL = import.meta.env.VITE_BASE_ZCONNECT_URL;
// @ts-expect-error -- untyped module
const VITE_BASE_ZCONNECT_V4_URL = import.meta.env.VITE_BASE_ZCONNECT_V4_URL;

type MobileViewLoaderProps = {
  url: string;
};

export type MobileViewRef = {
  resetIframeSession: () => void;
};

const MobileView = forwardRef<MobileViewRef, MobileViewLoaderProps>(
  ({ url }, ref) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const userInfo = useUserInfo();

    const SKINNY_WEB_URL = lowerCaseComparison(userInfo.groupId, "Z5DEMO_F")
      ? VITE_BASE_ZCONNECT_V4_URL
      : VITE_BASE_ZCONNECT_URL;

    useEffect(() => {
      const handleMessage = (event: MessageEvent) => {
        if (event.data.type === "RESET_GUEST_SESSION_SUCCESS") {
          // console.log('Debug: IN Parent: Session cleared in child done.');
          closeMobileView();
        }
      };

      window.addEventListener("message", handleMessage);

      return () => {
        window.removeEventListener("message", handleMessage);
      };
    }, []);

    useEffect(() => {
      if (iframeRef.current && url) {
        iframeRef.current.src = url;
      }
    }, [url]);

    const resetIframeSession = useCallback(() => {
      const targetOrigin = SKINNY_WEB_URL as string;
      // console.log("Resetting iframe session...", targetOrigin);

      if (!targetOrigin || !iframeRef.current?.contentWindow) {
        // console.warn("RESET_GUEST_SESSION skipped:", { targetOrigin, hasFrame: !!iframeRef.current?.contentWindow });
        closeMobileView();
        return;
      }

      iframeRef.current.contentWindow.postMessage(
        {
          type: "RESET_GUEST_SESSION",
          reason: "new-guest-flow",
          timestamp: Date.now(),
        },
        targetOrigin,
      );
    }, []);

    // Expose the resetIframeSession function to parent components via ref
    useImperativeHandle(
      ref,
      () => ({
        resetIframeSession,
      }),
      [resetIframeSession],
    );

    return (
      <iframe
        ref={iframeRef}
        className="size-full"
        allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
        // sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-downloads"
      />
    );
  },
);

MobileView.displayName = "MobileView";

export default MobileView;
